
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, XCircle, Mail, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const VerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const status = searchParams.get('status');
    const [email, setEmail] = useState('');
    const [resending, setResending] = useState(false);
    const { t } = useTranslation();
    const { toast } = useToast();
    const navigate = useNavigate();

    const handleResend = async (e) => {
        e.preventDefault();
        if (!email) return;

        setResending(true);
        try {
            await authAPI.resendVerification(email);
            toast({
                title: "Email Sent",
                description: "If an account exists with this email, a verification link has been sent.",
            });
            // Clear email after sending
            setEmail('');
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to send verification email. Please try again later.",
                variant: "destructive"
            });
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center p-4">
            <Card className="w-full max-w-md bg-[#1a1a1b] border-white/10 text-white">
                <CardHeader className="text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/5">
                        {status === 'ok' ? (
                            <CheckCircle2 className="h-6 w-6 text-green-500" />
                        ) : (
                            <XCircle className="h-6 w-6 text-red-500" />
                        )}
                    </div>
                    <CardTitle className="text-2xl font-bold">
                        {status === 'ok' ? 'Email Verified' : 'Verification Failed'}
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                        {status === 'ok'
                            ? 'Your email has been successfully verified.'
                            : 'The verification link is invalid or has expired.'}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {status === 'ok' ? (
                        <Button
                            className="w-full bg-[#2563EB] hover:bg-[#1d4ed8]"
                            onClick={() => navigate('/login')}
                        >
                            Continue to Login
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-400 text-center">
                                Please enter your email address to receive a new verification link.
                            </p>
                            <form onSubmit={handleResend} className="space-y-3">
                                <div className="relative">
                                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <input
                                        type="email"
                                        placeholder="name@example.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 bg-[#0a0a0b] border border-white/10 rounded-md focus:outline-none focus:border-[#2563EB] text-sm"
                                        required
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full"
                                    disabled={resending}
                                >
                                    {resending ? 'Sending...' : 'Resend Verification Email'}
                                </Button>
                            </form>
                            <div className="text-center">
                                <Link to="/login" className="text-sm text-gray-400 hover:text-white transition-colors">
                                    Back to Login
                                </Link>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default VerifyEmail;
